import React from 'react';
import { isEqual, omit } from 'lodash';
import {
  CoachAvailabilityInput,
  CoachAvailabilityExceptionInput,
  CoachAvailabilityInterval,
  DayOfWeek,
} from 'globalTypes';
import {
  GetCoachAvailabilities_coaching_coachAvailability_availabilities,
  GetCoachAvailabilities_coaching_coachAvailability_availabilityExceptions,
} from 'coaching/queries/__generated__/GetCoachAvailabilities';
import CoachingAvailabilityAddTimeOffView from './CoachingAvailabilityAddTimeOffView';
import CoachingAvailabilityEditView from './CoachingAvailabilityEditView';
import CoachingAvailabilityReadView from './CoachingAvailabilityReadView';
import CoachingConstants from 'coaching/CoachingConstants';
import CoachingView from 'coaching/components/CoachingView';
import { constructAvailabilitiesByDay } from 'coaching/lib/coachAvailability';

export type Props = {
  existingAvailabilities?: (GetCoachAvailabilities_coaching_coachAvailability_availabilities | null)[];
  existingAvailabilityExceptions?: (GetCoachAvailabilities_coaching_coachAvailability_availabilityExceptions | null)[];
  setCoachingAvailability: (
    input: CoachAvailabilityInput[],
    timezone: string,
    onMutate?: () => void
  ) => void;
  setCoachingAvailabilityException: (
    input: CoachAvailabilityExceptionInput[],
    timezone: string,
    onMutate?: () => void
  ) => void;
  deleteCoachingAvailabilityException: (exceptionUuid: string) => void;
  weekOffset: number;
  onAdvanceWeekOffset: () => void;
  onRegressWeekOffset: () => void;
  timezone: string;
  loadingSchedule: boolean;
  errorMessage: string | undefined;
};

type State = {
  availabilitiesByDay: CoachAvailabilityInput[];
  editAvailabilities: boolean;
  showAddTimeOffView: boolean;
  editMaxSessions: boolean;
  timezone: string;
};

export default class CoachingAvailabilityView extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      availabilitiesByDay: [],
      editAvailabilities: false,
      showAddTimeOffView: false,
      editMaxSessions: false,
      timezone: this.props.timezone,
    };
  }

  componentDidMount() {
    const { existingAvailabilities, loadingSchedule } = this.props;
    const availabilitiesByDay = constructAvailabilitiesByDay(existingAvailabilities);

    // default to the edit view if the coach has not set any availabilities yet
    const editAvailabilities =
      !loadingSchedule && (!existingAvailabilities || existingAvailabilities.length === 0);
    // show maxSession inputs by default if the user has some already set
    const editMaxSessions =
      !loadingSchedule && !!existingAvailabilities?.some(a => a?.maxSessions && a.maxSessions > 0);

    this.setState({
      editAvailabilities,
      availabilitiesByDay,
      editMaxSessions,
    });
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { existingAvailabilities, timezone } = this.props;

    if (
      isEqual(prevProps.existingAvailabilities, existingAvailabilities) &&
      isEqual(prevProps.timezone, timezone)
    ) {
      return;
    }

    const availabilitiesByDay = constructAvailabilitiesByDay(existingAvailabilities);
    this.setState({
      availabilitiesByDay,
      timezone,
    });

    // only recalculate the editAvailabilities state and initial editMaxSessions state
    // if the user is not already looking at the edit availability view
    if (prevState.editAvailabilities !== true) {
      // default to the edit view if the coach has not set any availabilities yet
      const editAvailabilities = !existingAvailabilities || existingAvailabilities.length === 0;
      // show maxSession inputs by default if the user has some already set
      const editMaxSessions = !!existingAvailabilities?.some(
        a => a?.maxSessions && a.maxSessions > 0
      );

      this.setState({ editAvailabilities, editMaxSessions });
    }
  }

  showEditView = () => {
    this.setState({ editAvailabilities: true });
  };

  showReadView = () => {
    this.setState({ editAvailabilities: false });
  };

  showAddTimeOffView = () => {
    this.setState({ showAddTimeOffView: true });
  };

  hideAddTimeOffView = () => {
    this.setState({ showAddTimeOffView: false });
  };

  onTimezoneChange = (newTimezone: string) => {
    this.setState({ timezone: newTimezone });
  };

  updateAvailabilitiesByDay = (
    dayOfWeek: DayOfWeek,
    intervals: CoachAvailabilityInterval[],
    maxSessions?: number
  ) => {
    const dayOfWeekIndex = CoachingConstants.SORTED_DAYS_OF_WEEK.indexOf(dayOfWeek);
    const { availabilitiesByDay } = this.state;
    const availabilityUuid = availabilitiesByDay[dayOfWeekIndex]?.uuid ?? '';

    // construct the updated availability
    const updatedAvailability: CoachAvailabilityInput = {
      uuid: availabilityUuid,
      timeSlot: { dayOfWeek, intervals },
    };
    // if maxSessions is undefined it means it was not updated.
    // if it's 0, we save it as undefined so the dropdowns will display the grey
    // "Any" placeholder text rather than the black "Any" that corresponds to value 0.
    if (maxSessions !== undefined) {
      updatedAvailability.maxSessions = maxSessions || undefined;
    }

    // replace the availability
    availabilitiesByDay.splice(dayOfWeekIndex, 1, updatedAvailability);
    this.setState({ availabilitiesByDay });
  };

  toggleEditMaxSessions = () => {
    const { editMaxSessions } = this.state;
    if (editMaxSessions) {
      // if we're toggling off, clear maxSessions on all availabilities
      this.setState(({ availabilitiesByDay }) => ({
        availabilitiesByDay: availabilitiesByDay.map(a => omit(a, 'maxSessions')),
      }));
    }
    this.setState({ editMaxSessions: !editMaxSessions });
  };

  renderEditView = () => {
    const { timezone, availabilitiesByDay, editMaxSessions } = this.state;
    return (
      <CoachingAvailabilityEditView
        onTimezoneChange={this.onTimezoneChange}
        onAvailabilityChange={this.updateAvailabilitiesByDay}
        onClickSetAvailability={() =>
          this.props.setCoachingAvailability(availabilitiesByDay, timezone, this.showReadView)
        }
        {...{ timezone, availabilitiesByDay, editMaxSessions }}
        toggleEditMaxSessions={this.toggleEditMaxSessions}
      />
    );
  };

  renderReadView = () => {
    const {
      loadingSchedule,
      errorMessage,
      weekOffset,
      existingAvailabilityExceptions,
    } = this.props;
    const { availabilitiesByDay } = this.state;

    return (
      <CoachingAvailabilityReadView
        errorMessage={errorMessage}
        loadingSchedule={loadingSchedule}
        existingAvailabilities={availabilitiesByDay}
        existingAvailabilityExceptions={existingAvailabilityExceptions}
        weekOffset={weekOffset}
        onClickLeftChevron={this.props.onRegressWeekOffset}
        onClickRightChevron={this.props.onAdvanceWeekOffset}
        onClickEditLink={this.showEditView}
        onClickAddTimeOff={this.showAddTimeOffView}
        deleteCoachingAvailabilityException={this.props.deleteCoachingAvailabilityException}
      />
    );
  };

  renderAddTimeOffView = () => {
    return (
      <CoachingAvailabilityAddTimeOffView
        weekOffset={this.props.weekOffset}
        onHide={this.hideAddTimeOffView}
        onAddTimeOff={this.props.setCoachingAvailabilityException}
      />
    );
  };

  render() {
    const availabilityView = this.state.editAvailabilities
      ? this.renderEditView()
      : this.renderReadView();
    return (
      // isCoach is always true since this is a coach-only view
      <CoachingView isCoach>
        {this.state.showAddTimeOffView ? this.renderAddTimeOffView() : availabilityView}
      </CoachingView>
    );
  }
}
